// src/components/MonitoringCharts.js
import React, { useState, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { ChevronLeft, ChevronRight, TrendingUp } from 'lucide-react';
import './MonitoringCharts.css';

const COLORS = { ok: '#22c55e', warning: '#f59e0b', critical: '#ef4444', neutral: '#cbd5e1' };

export default function MonitoringCharts({ patients, chartConfig }) {
    // Configuração completa incluindo o Score Geral
    const fullConfig = [
        { id: 'score', label: 'Desempenho Geral', icon: <TrendingUp size={16}/> },
        ...chartConfig
    ];

    const [index, setIndex] = useState(0);
    const current = fullConfig[index];

    const data = useMemo(() => {
        if (!patients.length) return null;

        let totalScore = 0;
        const counts = {};

        // Inicializa contadores
        chartConfig.forEach(cfg => {
            counts[cfg.id] = { ok: 0, warning: 0, critical: 0, neutral: 0 };
        });

        patients.forEach(p => {
            const s = p.status || {};
            totalScore += (s.score || 0);
            
            chartConfig.forEach(cfg => {
                // CORREÇÃO AQUI: Verifica se é objeto ou string direta
                const rawStatus = s[cfg.id];
                let val = 'critical';

                if (rawStatus) {
                    if (typeof rawStatus === 'string') {
                        val = rawStatus; // Formato antigo ('ok')
                    } else if (typeof rawStatus === 'object' && rawStatus.status) {
                        val = rawStatus.status; // Formato novo ({ status: 'ok', ... })
                    }
                }
                
                // Se o status retornado não estiver na lista (ex: neutral), trata com segurança
                if (counts[cfg.id][val] !== undefined) {
                    counts[cfg.id][val]++;
                }
            });
        });

        const getPieData = (key) => [
            { name: 'Em dia', value: counts[key].ok, color: COLORS.ok },
            { name: 'Atenção', value: counts[key].warning, color: COLORS.warning },
            { name: 'Atrasado', value: counts[key].critical, color: COLORS.critical },
            // Opcional: mostrar neutros se quiser, mas geralmente escondemos no gráfico
        ].filter(d => d.value > 0);

        const chartsData = {};
        chartConfig.forEach(cfg => {
            chartsData[cfg.id] = getPieData(cfg.id);
        });

        return {
            avgScore: (totalScore / patients.length).toFixed(0),
            charts: chartsData
        };

    }, [patients, chartConfig]);

    const handleNext = () => setIndex((prev) => (prev + 1) % fullConfig.length);
    const handlePrev = () => setIndex((prev) => (prev - 1 + fullConfig.length) % fullConfig.length);

    if (!data) return null;

    return (
        <div className="mini-chart-widget">
            <div className="widget-header-mini">
                <button onClick={handlePrev} className="widget-nav-btn"><ChevronLeft size={16}/></button>
                <div className="widget-title">
                    {current.icon}
                    <span>{current.label}</span>
                </div>
                <button onClick={handleNext} className="widget-nav-btn"><ChevronRight size={16}/></button>
            </div>

            <div className="widget-content-mini">
                {current.id === 'score' ? (
                    <div className="score-display">
                        <div className="score-circle" style={{
                            borderColor: data.avgScore >= 75 ? COLORS.ok : (data.avgScore >= 50 ? COLORS.warning : COLORS.critical),
                            color: data.avgScore >= 75 ? '#15803d' : (data.avgScore >= 50 ? '#b45309' : '#b91c1c')
                        }}>
                            <span className="score-number">{data.avgScore}</span>
                            <span className="score-unit">pts</span>
                        </div>
                        <p className="score-subtitle">Média da Área</p>
                    </div>
                ) : (
                    <div className="pie-display">
                        <div style={{ width: '100px', height: '100px' }}>
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie 
                                        data={data.charts[current.id]} 
                                        innerRadius={35} 
                                        outerRadius={50} 
                                        paddingAngle={2} 
                                        dataKey="value"
                                        stroke="none"
                                    >
                                        {data.charts[current.id].map((entry, i) => (
                                            <Cell key={i} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="mini-legend">
                            {data.charts[current.id].map(d => (
                                <div key={d.name} className="legend-item">
                                    <span className="dot" style={{background: d.color}}></span>
                                    <span>{d.name}: <strong>{d.value}</strong></span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}